import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = auth

  useEffect(() => {
    // Session lives in an httpOnly cookie set by the backend — just ask who we are.
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const applyAuth = (data) => {
    setUser(data.user);
  };

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data.requires_2fa) return { requires_2fa: true, challenge: res.data.challenge };
    applyAuth(res.data);
    return res.data.user;
  };

  const verify2fa = async (challenge, code) => {
    const res = await api.post("/auth/2fa/verify", { challenge, code });
    applyAuth(res.data);
    return res.data.user;
  };

  const loginWithGoogle = async (credential) => {
    const res = await api.post("/auth/google", { credential });
    if (res.data.requires_2fa) return { requires_2fa: true, challenge: res.data.challenge };
    applyAuth(res.data);
    return res.data.user;
  };

  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await api.post("/auth/register", { name, email, password });
    applyAuth(res.data);
    return res.data.user;
  };

  const acceptAccountantInvite = async (payload) => {
    const res = await api.post("/accountant/accept", payload);
    applyAuth(res.data);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, verify2fa, loginWithGoogle, refreshUser, logout, acceptAccountantInvite }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
